<template>
    <DsCheckbox
        v-model="autoRetrieve"
        class="mb-2"
        label="Automatically retrieve payoff statement"
        name="auto-retrieve"
        value="auto-retrieve"
    />

    <div class="pl-8">
        <p class="mb-4 text-content-sm text-content-primary">
            Once the client provides their mortgage information, Closinglock will use their loan details to order the payoff
            directly from the lender then notify you when it's ready.
        </p>

        <template v-if="autoRetrieve">
            <DsAlertBanner
                v-if="isCloseDateTooSoon"
                class="mb-4"
                title="Payoff statement retrieval may delay your closing"
                variant="warning"
            >
                Standard lender turnaround times range from one to several days. Because your closing date is approaching, this
                statement may not arrive in time. You can still submit, or handle this manually with the lender.
            </DsAlertBanner>

            <div
                class="mb-3 grid grid-cols-1 gap-4"
                :class="{ 'auto-cols-max md:grid-flow-col md:grid-cols-none': sideBySideDates }"
            >
                <DsDatePicker
                    v-model="localCloseDate"
                    :input="{ label: 'Close date', required: true }"
                    :maxValue="closeDateMaxValue"
                    :minValue="createToday()"
                />

                <DsDatePicker
                    v-model="localGoodThroughDate"
                    :input="{ label: 'Good through date', required: true }"
                    :minValue="localCloseDate ? addBusinessDays(localCloseDate, 1) : createToday()"
                />
            </div>

            <DsAlertBanner variant="info">
                We'll request the payoff using your selected good through date, but some lenders may provide a different date.
            </DsAlertBanner>
        </template>
    </div>
</template>

<script setup lang="ts">
import DsAlertBanner from '@/components/design-system/alert-banner/DsAlertBanner.vue';
import DsDatePicker from '@/components/design-system/form/date-picker/DsDatePicker.vue';
import DsCheckbox from '@/components/design-system/form/input/DsCheckbox.vue';
import { diffDaysForDate } from '@/components/Utils/Helper';
import { addBusinessDays, createToday, subtractBusinessDays } from '@/shared/utils/calendar-date-utils';
import type { DateValue } from 'reka-ui';
import { computed, watch } from 'vue';

defineProps<{
    /** Renders the close/good-through date inputs side by side instead of stacked, to match the consuming layout. */
    sideBySideDates?: boolean;
}>();

const autoRetrieve = defineModel<boolean>('autoRetrieve');
const localCloseDate = defineModel<DateValue>('localCloseDate');
const localGoodThroughDate = defineModel<DateValue>('localGoodThroughDate');

const isCloseDateTooSoon = computed(() => {
    if (!localCloseDate.value) return false;
    return diffDaysForDate([], localCloseDate.value.toString(), 'YYYY-MM-DD') <= 7;
});

// Maximum allowed close date when the user manually sets a good-through date (good-through − 7 business days).
const closeDateMaxValue = computed(() => {
    if (!localGoodThroughDate.value || !localCloseDate.value) return;

    // Skip constraint when good-through is auto-calculated — constraining it would block all future close dates.
    const isAutoSynced = localGoodThroughDate.value.compare(addBusinessDays(localCloseDate.value, 7)) === 0;

    if (isAutoSynced) return;

    return subtractBusinessDays(localGoodThroughDate.value, 7);
});

watch(localCloseDate, newDate => {
    localGoodThroughDate.value = newDate ? addBusinessDays(newDate, 7) : undefined;
});
</script>
